import { useState } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  Trophy, 
  Users, 
  BarChart3,
  Search,
  Crown,
  Award,
  Zap,
  Loader2,
  Plus,
  Minus,
  Trash2,
  Bug,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface LevelEntry {
  id: string;
  userId: string;
  level: number;
  xp: number;
  user: {
    id: string;
    username: string;
    avatar: string | null;
    discordId: string;
  } | null;
}

interface LevelingStats {
  totalUsers: number;
  averageLevel: number;
  topLevel: number;
}

export const LevelingPlugin = ({ serverId }: { serverId: string }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<LevelEntry | null>(null);
  const [xpAmount, setXpAmount] = useState(100);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Fetch leaderboard
  const { data: leaderboard = [], isLoading: leaderboardLoading } = useQuery<LevelEntry[]>({
    queryKey: ['servers', serverId, 'leveling', 'leaderboard'],
    queryFn: async () => {
      const { levelingApi } = await import('@/lib/api');
      return levelingApi.getLeaderboard(serverId, 50);
    },
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<LevelingStats>({
    queryKey: ['servers', serverId, 'leveling', 'stats'],
    queryFn: async () => {
      const { levelingApi } = await import('@/lib/api');
      return levelingApi.getStats(serverId);
    },
  });

  // Check plugin enabled status
  const { data: pluginStatus } = useQuery({
    queryKey: ['servers', serverId, 'plugins', 'leveling'],
    queryFn: async () => {
      const { pluginApi } = await import('@/lib/api');
      const plugins = await pluginApi.getPlugins(serverId);
      return plugins.find((p: any) => p.pluginId === 'leveling');
    },
  });

  // Enable plugin mutation
  const enablePluginMutation = useMutation({
    mutationFn: async () => {
      const { pluginApi } = await import('@/lib/api');
      return pluginApi.enablePlugin(serverId, 'leveling');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'leveling'] });
    },
  });

  const togglePluginMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { pluginApi } = await import('@/lib/api');
      return enabled
        ? pluginApi.enablePlugin(serverId, 'leveling')
        : pluginApi.disablePlugin(serverId, 'leveling');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins', 'leveling'] });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'leveling'] });
    },
  });

  // Diagnostics query
  const { data: diagnostics, refetch: refetchDiagnostics, isLoading: diagnosticsLoading } = useQuery({
    queryKey: ['servers', serverId, 'diagnostics', 'leveling'],
    queryFn: async () => {
      const { diagnosticsApi } = await import('@/lib/api');
      return diagnosticsApi.getLevelingDiagnostics(serverId);
    },
    enabled: showDiagnostics,
  });

  // Force enable mutation
  const forceEnableMutation = useMutation({
    mutationFn: async () => {
      const { diagnosticsApi } = await import('@/lib/api');
      return diagnosticsApi.forceEnableLeveling(serverId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId] });
      refetchDiagnostics();
    },
  });

  // Update XP mutation
  const updateXpMutation = useMutation({
    mutationFn: async ({ userId, amount }: { userId: string; amount: number }) => {
      const { levelingApi } = await import('@/lib/api');
      return levelingApi.updateXp(serverId, userId, amount);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'leveling'] });
      setSelectedUser(null);
      setXpAmount(100);
    },
  });

  // Reset user mutation
  const resetUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { levelingApi } = await import('@/lib/api');
      return levelingApi.resetUser(serverId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'leveling'] });
      setSelectedUser(null);
    },
  });

  const filteredLeaderboard = leaderboard.filter(entry => 
    entry.user?.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateXpForNextLevel = (level: number) => {
    return 5 * (level ** 2) + 50 * level + 100;
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-amber-400" />;
      case 2:
        return <Award className="w-5 h-5 text-slate-400" />;
      case 3:
        return <Trophy className="w-5 h-5 text-orange-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-primary" />
            Sistema Livelli
          </h2>
          <p className="text-muted-foreground mt-1">
            Visualizza la classifica e gestisci i livelli degli utenti
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={pluginStatus?.enabled ?? false}
            onCheckedChange={(enabled) => togglePluginMutation.mutate(enabled)}
            disabled={togglePluginMutation.isPending}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDiagnostics(!showDiagnostics)}
          >
            <Bug className="w-4 h-4 mr-2" />
            {showDiagnostics ? 'Nascondi' : 'Diagnostica'}
          </Button>
        </div>
      </div>

      {/* Diagnostics Panel */}
      {showDiagnostics && (
        <Card className="glass-card border-blue-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-500">
              <Bug className="w-5 h-5" />
              Diagnostica Plugin Leveling
            </CardTitle>
            <CardDescription>
              Controlla lo stato del plugin e risolvi eventuali problemi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {diagnosticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : diagnostics?.success ? (
              <div className="space-y-4">
                {/* Plugin Status */}
                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Plugin nel Database</span>
                    {diagnostics.diagnostics.plugin.inDatabase ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Plugin Abilitato</span>
                    {diagnostics.diagnostics.plugin.enabled ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Plugin Caricato in Memoria</span>
                    {diagnostics.diagnostics.runtime.pluginLoaded ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Bot nel Server</span>
                    {diagnostics.diagnostics.discord.botInGuild ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>

                {/* Data Status */}
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="font-medium mb-2">Dati Leveling</p>
                  <p className="text-sm text-muted-foreground">
                    Utenti con XP: {diagnostics.diagnostics.data.levelEntriesCount}
                  </p>
                  {diagnostics.diagnostics.discord.botInGuild && (
                    <p className="text-sm text-muted-foreground">
                      Membri nel server: {diagnostics.diagnostics.discord.guildMemberCount}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => refetchDiagnostics()}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Aggiorna
                  </Button>
                  {!diagnostics.diagnostics.plugin.enabled && (
                    <Button
                      onClick={() => forceEnableMutation.mutate()}
                      disabled={forceEnableMutation.isPending}
                      className="flex-1 btn-gradient border-0"
                    >
                      {forceEnableMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Forza Abilitazione
                    </Button>
                  )}
                </div>

                {/* Warnings */}
                {!diagnostics.diagnostics.discord.botInGuild && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-500">Bot non presente nel server</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Il bot non è presente nel server Discord. Invita il bot prima di usare questo plugin.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-red-500">
                <XCircle className="w-8 h-8 mx-auto mb-2" />
                <p>Errore nel caricamento della diagnostica</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Debug Info */}
      {pluginStatus && !pluginStatus.enabled && (
        <Card className="glass-card border-amber-500/50 bg-amber-500/10">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Zap className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-500 mb-1">Plugin Leveling Disabilitato</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Il plugin leveling non è attualmente abilitato per questo server. 
                  I messaggi non verranno tracciati fino a quando non verrà attivato.
                </p>
                <Button
                  onClick={() => enablePluginMutation.mutate()}
                  disabled={enablePluginMutation.isPending}
                  className="btn-gradient border-0"
                  size="sm"
                >
                  {enablePluginMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Attivazione...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Abilita Plugin Leveling
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utenti Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalUsers}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Livello Medio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.averageLevel.toFixed(1)}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Livello Massimo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.topLevel}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Cerca utente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-sidebar-accent border border-sidebar-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Leaderboard */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Classifica</CardTitle>
          <CardDescription>
            Top {filteredLeaderboard.length} utenti per XP totale
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leaderboardLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredLeaderboard.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">
                {searchTerm ? "Nessun utente trovato" : "Nessun utente in classifica"}
              </p>
              {!searchTerm && pluginStatus?.enabled && (
                <p className="text-sm text-muted-foreground">
                  Gli utenti inizieranno a guadagnare XP quando invieranno messaggi nel server.
                  <br />
                  Assicurati che il bot sia presente nel server e che il plugin sia abilitato.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLeaderboard.map((entry, index) => {
                const rank = index + 1;
                const xpForNext = calculateXpForNextLevel(entry.level);
                const progress = (entry.xp / xpForNext) * 100;

                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedUser(entry)}
                  >
                    {/* Rank */}
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0",
                      rank === 1 && "bg-gradient-to-br from-amber-400 to-amber-600 text-white",
                      rank === 2 && "bg-gradient-to-br from-slate-300 to-slate-500 text-white",
                      rank === 3 && "bg-gradient-to-br from-orange-400 to-orange-600 text-white",
                      rank > 3 && "bg-muted text-muted-foreground"
                    )}>
                      {getRankIcon(rank) || rank}
                    </div>

                    {/* User Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold flex-shrink-0">
                      {entry.user?.username?.charAt(0).toUpperCase() || '?'}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{entry.user?.username || 'Unknown User'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Zap className="w-3 h-3 text-primary" />
                        <span className="text-sm text-muted-foreground">
                          Livello {entry.level} • {entry.xp.toLocaleString()} XP
                        </span>
                      </div>
                      {/* Progress Bar */}
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-2">
                        <div 
                          className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300" 
                          style={{ width: `${Math.min(100, progress)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {entry.xp} / {xpForNext} XP al prossimo livello
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Management Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-6 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                {selectedUser.user?.username?.charAt(0).toUpperCase() || '?'}
              </div>
              <h3 className="text-xl font-bold">{selectedUser.user?.username}</h3>
              <p className="text-muted-foreground">
                Livello {selectedUser.level} • {selectedUser.xp.toLocaleString()} XP
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Modifica XP
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setXpAmount(Math.max(1, xpAmount - 100))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <input
                    type="number"
                    value={xpAmount}
                    onChange={(e) => setXpAmount(parseInt(e.target.value) || 0)}
                    className="flex-1 px-4 py-2 bg-sidebar-accent border border-sidebar-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setXpAmount(xpAmount + 100)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => updateXpMutation.mutate({ 
                    userId: selectedUser.userId, 
                    amount: xpAmount 
                  })}
                  disabled={updateXpMutation.isPending}
                  className="flex-1 btn-gradient border-0"
                >
                  {updateXpMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi XP
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => updateXpMutation.mutate({ 
                    userId: selectedUser.userId, 
                    amount: -xpAmount 
                  })}
                  disabled={updateXpMutation.isPending}
                  variant="outline"
                  className="flex-1"
                >
                  {updateXpMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Minus className="w-4 h-4 mr-2" />
                      Rimuovi XP
                    </>
                  )}
                </Button>
              </div>

              <Button
                onClick={() => {
                  if (confirm('Sei sicuro di voler resettare questo utente?')) {
                    resetUserMutation.mutate(selectedUser.userId);
                  }
                }}
                disabled={resetUserMutation.isPending}
                variant="destructive"
                className="w-full"
              >
                {resetUserMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Reset Utente
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                onClick={() => setSelectedUser(null)}
                className="w-full"
              >
                Chiudi
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
